'use client';

/**
 * デバッグIssue報告ボタン
 *
 * 画面右上に浮動表示され、クリックでIssue報告モーダルを開く
 * dev/sandbox環境でのみ表示
 *
 * クリック時の動作:
 * 1. スクリーンショットをキャプチャ
 * 2. クリップボードにコピー
 * 3. モーダルを開く（事前キャプチャしたスクショを渡す）
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bug, Loader2, X } from 'lucide-react';
import { DebugIssueModal } from './DebugIssueModal';
import { initConsoleCapture, captureScreenshot, copyScreenshotToClipboard } from '@/lib/debug';
import type { ScreenshotData } from '@/lib/debug';
import { logger } from '@/lib/logger';

/**
 * 表示を許可する環境かどうかを判定（クライアントサイド）
 * - dev, local, sandbox-* で表示
 * - Vercel preview 環境で表示
 * - localhost で表示
 * - staging, prod では非表示
 */
function isAllowedEnvironment(): boolean {
  function isHiddenStage(stage: string | undefined): boolean {
    return stage === 'prod' || stage === 'staging' || stage === 'demo';
  }

  function isDebugHostname(hostname: string): boolean {
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('sandbox')
    );
  }

  function isDebugVercelEnv(vercelEnv: string | undefined): boolean {
    return vercelEnv === 'preview' || vercelEnv === 'development';
  }

  function isDebugStage(stage: string | undefined): boolean {
    return stage === 'dev' || stage === 'local' || stage?.startsWith('sandbox') === true;
  }

  // クライアントサイドのみで実行
  if (typeof window === 'undefined') {
    return false;
  }

  const stage = process.env.NEXT_PUBLIC_STAGE;
  const nodeEnv = process.env.NODE_ENV;
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
  const hostname = window.location.hostname;

  if (isHiddenStage(stage)) {
    return false;
  }

  return (
    isDebugHostname(hostname) ||
    isDebugVercelEnv(vercelEnv) ||
    isDebugStage(stage) ||
    nodeEnv === 'development'
  );
}

export function DebugIssueButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [preloadedScreenshot, setPreloadedScreenshot] = useState<ScreenshotData | null>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // 環境チェック
    if (isAllowedEnvironment()) {
      setIsVisible(true);
      // コンソールキャプチャを初期化
      initConsoleCapture();
    }
  }, []);

  useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  // ボタンクリック時: スクショ取得 → クリップボードコピー → モーダルオープン
  const handleButtonClick = useCallback(async () => {
    setIsCapturing(true);

    try {
      // スクリーンショットをキャプチャ
      const screenshot = await captureScreenshot();
      setPreloadedScreenshot(screenshot);

      // クリップボードにコピー
      await copyScreenshotToClipboard(screenshot.dataUrl);

      // モーダルを開く
      setIsOpen(true);
    } catch (error) {
      logger.warn('law.debug.screenshot_capture.violated', {
        component: 'DebugIssueButton',
        error,
      });
      // スクショ失敗してもモーダルは開く
      setPreloadedScreenshot(null);
      setIsOpen(true);
    } finally {
      setIsCapturing(false);
    }
  }, []);

  // キーボードショートカット (Ctrl+Shift+I)
  useEffect(() => {
    if (!isVisible || isDismissed) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        handleButtonClick();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, isDismissed, handleButtonClick]);

  // モーダルを閉じるときにスクショをクリア
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPreloadedScreenshot(null);
  }, []);

  if (!isVisible) {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  const floatingButton = portalRoot
    ? createPortal(
        <div className="pointer-events-none fixed inset-0 z-[10000]">
          <div className="pointer-events-auto absolute bottom-5 right-5 flex flex-col items-end gap-1 md:bottom-6 md:right-6">
            <button
              type="button"
              onClick={() => setIsDismissed(true)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-white opacity-70 shadow transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
              aria-label="デバッグボタンを非表示"
              title="デバッグボタンを非表示"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleButtonClick}
              disabled={isCapturing}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg transition-all hover:bg-orange-600 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Issue報告"
              title="Issue報告 (Ctrl+Shift+I)"
            >
              {isCapturing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Bug className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>,
        portalRoot,
      )
    : null;

  return (
    <>
      {floatingButton}
      <DebugIssueModal
        isOpen={isOpen}
        onClose={handleClose}
        preloadedScreenshot={preloadedScreenshot}
      />
    </>
  );
}
